import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api';
import { useLoad } from '../hooks';
import { PageHead } from '../Layout';
import { DIMENSIONS, ScoreBar, num } from './Queue';
import { formatDateTime } from '../format';
import { hasTrendArticlePriority, totalWordCount, trendArticleSourcePost } from '../../shared/types';
import type { ArticleSummary, IdeaSummary, ScoreComponents } from '../../shared/types';

/**
 * One idea package: the claim, how it scored and why, and every verbatim
 * member excerpt it is made of, each with its provenance.
 */

interface ExcerptSource {
  kind: 'message' | 'media';
  author: string | null;
  date: string | null;
  text: string | null;
  title: string | null;
  mediaKind: string | null;
}

interface Excerpt {
  id: number;
  sourceType: 'message' | 'media' | 'source_doc';
  sourceId: number;
  quotedText: string;
  role: 'core' | 'supporting' | 'resonant';
  note: string | null;
  source: ExcerptSource | null;
}

interface IdeaPackage extends IdeaSummary {
  excerpts: Excerpt[];
  /** The existing composition record, never a client-side reconstruction. */
  preparedArticle: Pick<ArticleSummary, 'id' | 'title' | 'bodyMd' | 'coverMediaAssetId'> | null;
}

/** Once prepared, the article record owns the one title shown for the piece. */
export function canonicalArticleTitle(
  idea: Pick<IdeaSummary, 'id' | 'title'>,
  preparedArticle: Pick<ArticleSummary, 'title'> | null,
): string {
  return preparedArticle?.title?.trim() || idea.title?.trim() || `Idea ${idea.id}`;
}

function when(iso: string | null | undefined): string {
  if (!iso) return 'unknown date';
  return formatDateTime(iso) === '—' ? 'unknown date' : formatDateTime(iso);
}

function tweetCandidateText(
  idea: Pick<IdeaSummary, 'coreClaim' | 'title'> & { candidateText?: string | null },
  excerptText?: string | null,
): string {
  return (excerptText ?? idea.candidateText ?? idea.coreClaim ?? idea.title ?? '').trim();
}

function Provenance({ excerpt }: { excerpt: Excerpt }) {
  const s = excerpt.source;
  const author = s?.author ?? 'unknown member';
  const kindLabel =
    excerpt.sourceType === 'message'
      ? 'chat message'
      : excerpt.sourceType === 'media'
        ? `${s?.mediaKind ?? 'media'} transcript`
        : `document${s?.title ? ` · ${s.title}` : ''}`;

  const body = (
    <>
      {author} · {when(s?.date)} · {kindLabel}{' '}
      <span className="mono faint">
        {excerpt.sourceType}#{excerpt.sourceId}
      </span>
    </>
  );

  return (
    <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
      {excerpt.sourceType === 'message' ? body : <Link to="/library">{body}</Link>}
    </div>
  );
}

export default function IdeaDetail() {
  const { id } = useParams();
  const [idea, setIdea] = useState<IdeaPackage | null>(null);
  const [coverUrl, setCoverUrl] = useState<string | null>(null);

  const { loading, error } = useLoad(
    useCallback(async () => {
      setIdea(await api.get<IdeaPackage>(`/ideas/${id}`));
    }, [id]),
    // A scheduling transition can precede the Editor's creation of the
    // canonical article record. Refresh so the same expanded view switches to
    // the prepared article as soon as that record exists.
    { enabled: Boolean(id), pollMs: 15_000 },
  );

  useEffect(() => {
    const coverId = idea?.preparedArticle?.coverMediaAssetId;
    if (!coverId) {
      setCoverUrl(null);
      return;
    }

    let cancelled = false;
    api
      .get<{ url: string }>(`/media/${coverId}/url`)
      .then((response) => {
        if (!cancelled) setCoverUrl(response.url);
      })
      .catch(() => {
        if (!cancelled) setCoverUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [idea?.preparedArticle?.coverMediaAssetId]);

  if (loading) {
    return (
      <>
        <PageHead title={`Idea ${id ?? ''}`} />
        <div className="empty">Loading…</div>
      </>
    );
  }

  if (!idea) {
    return (
      <>
        <PageHead title={`Idea ${id ?? ''}`} />
        <p className="fail">{error ?? 'not found'}</p>
      </>
    );
  }

  if (idea.kind === 'tweet' || idea.kind === 'quote_tweet' || idea.kind === 'reply') {
    const excerpt = idea.excerpts[0];
    const candidate = tweetCandidateText(idea, excerpt?.quotedText);
    const quotedPost = idea.kind === 'reply' ? idea.meta?.replyTarget : idea.meta?.quotedPost;
    const sourceUrl =
      quotedPost &&
      typeof quotedPost === 'object' &&
      typeof (quotedPost as Record<string, unknown>).url === 'string'
        ? String((quotedPost as Record<string, unknown>).url)
        : null;
    const label = idea.kind === 'quote_tweet' ? 'Quote tweet' : idea.kind === 'reply' ? 'Reply' : 'Tweet';

    return (
      <>
        <PageHead
          title={label}
          subtitle={`${candidate.length} characters · ${idea.excerpts.length} ${idea.excerpts.length === 1 ? 'source' : 'sources'}`}
          actions={
            <>
              <span className="chip">{idea.kind}</span>
              <span
                className={`chip${idea.stage === 'scheduled' || idea.stage === 'published' ? ' on' : ''}`}
              >
                {idea.stage.replace('_', ' ')}
              </span>
            </>
          }
        />

        {error ? <p className="fail">{error}</p> : null}

        <p className="editorial-alert">
          This is a read-only historical record. Manage current work in <Link to="/dashboard">Publications</Link>.
        </p>

        {idea.kind !== 'tweet' && <p className="muted">This retired output is preserved as a read-only historical record.</p>}

        <section aria-labelledby="tweet-candidate-title" style={{ maxWidth: '68ch', marginBottom: 28 }}>
          <div className="label" id="tweet-candidate-title" style={{ marginBottom: 10 }}>
            Candidate
          </div>
          {candidate ? (
            <blockquote
              style={{
                margin: 0,
                paddingLeft: 16,
                borderLeft: '2px solid var(--line-strong)',
                fontSize: 16,
                lineHeight: 1.6,
                whiteSpace: 'pre-wrap',
              }}
            >
              {candidate}
            </blockquote>
          ) : (
            <div className="empty">No candidate text is recorded.</div>
          )}
          <div className="faint mono" style={{ fontSize: 12, marginTop: 8 }}>
            {candidate.length} characters
          </div>
        </section>

        {sourceUrl ? (
          <p style={{ margin: '-14px 0 28px' }}>
            <a href={sourceUrl} target="_blank" rel="noreferrer">
              {idea.kind === 'reply' ? 'Target post on X ↗' : 'Source post on X ↗'}
            </a>
          </p>
        ) : null}

        {excerpt ? (
          <section aria-labelledby="tweet-provenance-title">
            <div className="label" id="tweet-provenance-title" style={{ marginBottom: 8 }}>
              Source
            </div>
            <Provenance excerpt={excerpt} />
            {idea.excerpts.length > 1 ? (
              <div className="faint" style={{ fontSize: 12, marginTop: 6 }}>
                {idea.excerpts.length - 1} additional source{idea.excerpts.length === 2 ? '' : 's'} attached.
              </div>
            ) : null}
          </section>
        ) : null}
      </>
    );
  }

  const components = idea.scoreComponents as ScoreComponents | null;
  const words = totalWordCount(idea.excerpts.map((e) => e.quotedText));
  const title = canonicalArticleTitle(idea, idea.preparedArticle);
  const trendPost = trendArticleSourcePost(idea.meta);

  return (
    <>
      <PageHead
        title={title}
        subtitle={`${idea.excerpts.length} excerpt${idea.excerpts.length === 1 ? '' : 's'} · ${words} words`}
        actions={
          <>
            <span className="chip">{idea.kind}</span>
            {hasTrendArticlePriority(idea) ? <span className="chip">Timely</span> : null}
            <span
              className={`chip${idea.stage === 'scheduled' || idea.stage === 'published' ? ' on' : ''}`}
            >
              {idea.stage.replace('_', ' ')}
            </span>
          </>
        }
      />

      {error ? <p className="fail">{error}</p> : null}

      <p className="editorial-alert">
        This is a read-only historical record. Manage current work in <Link to="/dashboard">Publications</Link>.
      </p>

      {trendPost ? (
        <section style={{ maxWidth: '68ch', marginBottom: 24 }}>
          <div className="label">Trend source · embedded in the article</div>
          <blockquote style={{ margin: '12px 0', whiteSpace: 'pre-wrap' }}>{trendPost.text}</blockquote>
          <a href={trendPost.url} target="_blank" rel="noreferrer">View source post on X ↗</a>
        </section>
      ) : null}

      {idea.preparedArticle ? (
        <section aria-labelledby="prepared-article-title" style={{ maxWidth: '68ch', marginBottom: 32 }}>
          <div className="label" id="prepared-article-title" style={{ marginBottom: 10 }}>
            Prepared article
          </div>
          {coverUrl ? (
            <img
              src={coverUrl}
              alt={idea.preparedArticle.title?.trim() || 'Article cover'}
              style={{ display: 'block', width: '100%', maxWidth: 680, marginBottom: 18, border: '1px solid var(--line)' }}
            />
          ) : null}
          <div style={{ whiteSpace: 'pre-wrap', fontSize: 16, lineHeight: 1.65 }}>
            {idea.preparedArticle.bodyMd?.trim() || '(The prepared article has no body.)'}
          </div>
          <p style={{ margin: '14px 0 0' }}>
            <Link to={`/articles/${idea.preparedArticle.id}`}>Open article details →</Link>
          </p>
        </section>
      ) : idea.stage === 'scheduled' ? (
        <section className="empty" aria-labelledby="prepared-article-title" style={{ maxWidth: '68ch', marginBottom: 32, textAlign: 'left' }}>
          <div className="label" id="prepared-article-title" style={{ marginBottom: 8 }}>
            Article preparation incomplete
          </div>
          This historical package is missing its prepared draft.
        </section>
      ) : null}

      <div style={{ display: 'flex', gap: 48, flexWrap: 'wrap', marginBottom: 32 }}>
        <div style={{ flex: '0 0 auto' }}>
          <div className="label" style={{ marginBottom: 10 }}>
            Score {num(idea.score)}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'auto 120px 28px', gap: '7px 10px', alignItems: 'center' }}>
            {DIMENSIONS.map((d) => {
              const v = components ? Number(components[d.key]) : null;
              return (
                <div key={d.key} style={{ display: 'contents' }}>
                  <span className="muted" style={{ fontSize: 12 }}>
                    {d.label}
                  </span>
                  <ScoreBar value={v} label={d.label} width={120} />
                  <span className="mono" style={{ fontSize: 12, textAlign: 'right' }}>
                    {v === null ? '—' : v}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <div style={{ flex: '1 1 320px', minWidth: 280 }}>
          <div className="label" style={{ marginBottom: 8 }}>
            Core claim
          </div>
          <p style={{ margin: 0, maxWidth: '62ch' }}>
            {idea.coreClaim ?? <span className="faint">no claim recorded</span>}
          </p>
          {idea.scoreRationale ? (
            <>
              <div className="label" style={{ margin: '20px 0 8px' }}>
                Scoring rationale
              </div>
              <p className="muted" style={{ margin: 0, maxWidth: '62ch' }}>
                {idea.scoreRationale}
              </p>
            </>
          ) : null}
        </div>
      </div>

      <div className="label" style={{ marginBottom: 12 }}>
        Excerpts — verbatim member words
      </div>

      {idea.excerpts.length === 0 ? (
        <div className="empty">No excerpts attached.</div>
      ) : (
        <div style={{ borderTop: '1px solid var(--line)' }}>
          {idea.excerpts.map((e) => (
            <div key={e.id} style={{ padding: '18px 0', borderBottom: '1px solid var(--line)' }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', marginBottom: 8 }}>
                <span className="chip">{e.role}</span>
                <span className="faint mono" style={{ fontSize: 11 }}>
                  {e.quotedText.length} chars
                </span>
              </div>
              <blockquote
                style={{
                  margin: 0,
                  paddingLeft: 16,
                  borderLeft: '2px solid var(--line-strong)',
                  fontSize: 15,
                  lineHeight: 1.6,
                  maxWidth: '68ch',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {e.quotedText}
              </blockquote>
              <Provenance excerpt={e} />
              {e.note ? (
                <div className="faint" style={{ fontSize: 12, marginTop: 4 }}>
                  {e.note}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </>
  );
}
